/*
 * Guardian website chat widget.
 *
 *   <script src="https://chat.guardianaccess.com/widget.js" defer></script>
 *
 * Renders in a Shadow DOM so the site's CSS and the widget's never collide.
 * Every message is inserted as text (never HTML).
 */
import { io, type Socket } from 'socket.io-client';
import css from './widget.css?inline';

type Support = { phone: string; hours: string; email: string };
type Message = { id: number; sender: 'visitor' | 'agent' | 'system' | 'bot'; agentName: string | null; body: string; createdAt: string };
type Stored = { id: string; token: string; at: number };

const STORE_KEY = 'guardian-chat';
const RESUME_WINDOW_MS = 2 * 60 * 60 * 1000;

const script = document.currentScript as HTMLScriptElement | null;
const SERVER = script?.dataset.server || (script ? new URL(script.src).origin : location.origin);

function el<K extends keyof HTMLElementTagNameMap>(tag: K, attrs: Record<string, string> = {}, ...children: (Node | string)[]) {
  const node = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (k === 'class') node.className = v;
    else node.setAttribute(k, v);
  }
  node.append(...children);
  return node;
}

const store = {
  get(): Stored | null {
    try {
      const s = JSON.parse(localStorage.getItem(STORE_KEY) || 'null') as Stored | null;
      return s && Date.now() - s.at < RESUME_WINDOW_MS ? s : null;
    } catch {
      return null;
    }
  },
  set(v: Omit<Stored, 'at'>) {
    try {
      localStorage.setItem(STORE_KEY, JSON.stringify({ ...v, at: Date.now() }));
    } catch {
      /* private mode */
    }
  },
  clear() {
    try {
      localStorage.removeItem(STORE_KEY);
    } catch {
      /* private mode */
    }
  },
};

class GuardianChat {
  private root: ShadowRoot;
  private launcher!: HTMLButtonElement;
  private panel!: HTMLElement;
  private body!: HTMLElement;
  private socket: Socket | null = null;
  private support: Support = { phone: '(615) 255-4466', hours: 'Mon – Fri 7:30 AM – 4:30 PM ET', email: 'support@guardianaccess.com' };
  private state: 'idle' | 'form' | 'ringing' | 'active' | 'ended' | 'offline' = 'idle';
  private office: string | null = null;
  private agentName = '';
  private log: HTMLElement | null = null;
  private seen = new Set<number>();
  private typingEl: HTMLElement | null = null;
  private typingHide: number | undefined;
  private sentTyping = 0;
  private stopTyping: number | undefined;
  private audio: AudioContext | null = null;
  private unread = 0;
  private baseTitle = document.title;
  private titleTimer: number | undefined;

  constructor() {
    const host = el('div', { id: 'guardian-chat' });
    document.body.append(host);
    this.root = host.attachShadow({ mode: 'open' });
    this.root.append(el('style', {}, css));
    this.renderShell();
    const saved = store.get();
    if (saved) this.resume(saved);
  }

  /* ---------------------------------------------------------- Transport -- */

  private connect() {
    if (this.socket) return this.socket;
    this.socket = io(`${SERVER}/visitor`, { transports: ['websocket', 'polling'], withCredentials: false });
    this.socket.on('status', (s: { state: string; agentName?: string; support?: Support; office?: string | null }) => this.onStatus(s));
    this.socket.on('message', (m: Message) => this.addMessage(m));
    this.socket.on('typing', (t: { typing: boolean; name: string }) => this.showTyping(t.typing, t.name));
    this.socket.on('connect', () => {
      // After a network drop, re-attach to the conversation so agent replies keep arriving.
      const saved = store.get();
      if (saved) this.socket!.emit('chat:resume', saved, () => {});
    });
    return this.socket;
  }

  private emit<T>(event: string, payload?: unknown): Promise<T> {
    return new Promise((resolve) => this.connect().timeout(10_000).emit(event, payload, (err: unknown, res: T) => resolve(err ? ({ ok: false, error: 'Connection problem. Please try again.' } as T) : res)));
  }

  /* -------------------------------------------------------------- Shell -- */

  private renderShell() {
    this.launcher = el('button', { class: 'launcher', type: 'button', 'aria-label': 'Chat with us', 'aria-expanded': 'false' }) as HTMLButtonElement;
    this.launcher.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 5.5A2.5 2.5 0 0 1 6.5 3h11A2.5 2.5 0 0 1 20 5.5v8a2.5 2.5 0 0 1-2.5 2.5H10l-4.2 3.6c-.5.4-1.3.1-1.3-.6V16.9A2.5 2.5 0 0 1 4 16V5.5Z" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linejoin="round"/></svg><span>Chat with us</span>';
    this.launcher.addEventListener("click", () => this.toggle(this.panel.hidden === true));

    const close = el('button', { class: 'icon-btn', type: 'button', 'aria-label': 'Minimize chat' }, '–');
    close.addEventListener('click', () => this.toggle(false));
    this.body = el('div', { class: 'body' });
    this.panel = el(
      'section',
      { class: 'panel', role: 'dialog', 'aria-label': 'Chat with Guardian Access Solutions', hidden: '' },
      el('header', {}, el('div', {}, el('p', { class: 'eyebrow' }, 'Guardian Access Solutions'), el('h2', {}, 'Chat with our team')), close),
      this.body,
    );
    this.panel.addEventListener('keydown', (e) => e.key === 'Escape' && this.toggle(false));
    this.root.append(this.panel, this.launcher);
  }

  private async toggle(open: boolean) {
    this.panel.hidden = !open;
    this.launcher.setAttribute('aria-expanded', String(open));
    this.launcher.classList.toggle('is-open', open);
    if (!open) return;
    if (this.state === 'idle' || this.state === 'ended' || this.state === 'offline') {
      this.renderLoading();
      const s = await this.emit<{ online: boolean; support: Support }>('status');
      if (s && 'support' in s) this.support = s.support;
      if (s?.online) this.renderForm();
      else this.renderOffline();
    }
    (this.panel.querySelector('input,textarea,button.primary') as HTMLElement | null)?.focus();
  }

  /* ------------------------------------------------------------ Screens -- */

  private screen(...nodes: Node[]) {
    this.body.replaceChildren(...nodes);
    this.log = null;
  }

  private renderLoading() {
    this.screen(el('p', { class: 'muted center' }, 'One moment…'));
  }

  private renderOffline() {
    this.state = 'offline';
    store.clear();
    const digits = this.support.phone.replace(/\D/g, '').slice(-10);
    this.screen(
      el('div', { class: 'offline' },
        el('p', { class: 'lead' }, "Our team isn't available for chat right now."),
        el('p', {}, 'We’re happy to help by phone ', el('strong', {}, this.support.hours), ', or by email.'),
        el('a', { class: 'btn primary', href: `tel:+1${digits}` }, `Call ${this.support.phone}`),
        el('a', { class: 'btn', href: `mailto:${this.support.email}` }, `Email ${this.support.email}`),
      ),
    );
  }

  private renderForm(error = '') {
    this.state = 'form';
    const form = el('form', { class: 'form', novalidate: '' });
    const field = (name: string, label: string, attrs: Record<string, string> = {}) =>
      el('label', { class: 'field' }, el('span', {}, label), el('input', { name, ...attrs }));
    form.append(
      el('p', { class: 'muted' }, 'We’ll connect you with the team nearest your work site.'),
      field('name', 'Name', { autocomplete: 'name', required: '', maxlength: '120' }),
      field('email', 'Email', { type: 'email', autocomplete: 'email', required: '', maxlength: '200' }),
      field('zip', 'ZIP code of the work site', { inputmode: 'numeric', autocomplete: 'postal-code', required: '', maxlength: '10' }),
      el('input', { name: 'website', class: 'hp', tabindex: '-1', autocomplete: 'off', 'aria-hidden': 'true' }),
      el('p', { class: 'error', role: 'alert' }, error),
      el('button', { class: 'btn primary', type: 'submit' }, 'Start chat'),
      el('p', { class: 'fine' }, 'By starting a chat, you agree to our ', el('a', { href: `${location.origin}/privacy-policy/`, target: '_blank', rel: 'noopener' }, 'Privacy Policy'), '.'),
    );
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const data = Object.fromEntries(new FormData(form)) as Record<string, string>;
      this.unlockAudio();
      if (!data.name?.trim() || !data.email?.trim() || !data.zip?.trim()) return this.renderForm('Please enter your name, email, and ZIP code.');
      if (!/^\S+@\S+\.\S+$/.test(data.email.trim())) return this.renderForm('Please enter a valid email address.');
      if (!/^\d{5}(-?\d{4})?$/.test(data.zip.trim())) return this.renderForm('Please enter a 5-digit ZIP code.');
      (form.querySelector('button[type=submit]') as HTMLButtonElement).disabled = true;
      const r = await this.emit<{ ok: boolean; error?: string; id?: string; token?: string; state?: string; office?: string | null; offline?: Support; messages?: Message[] }>('chat:start', { ...data, pageUrl: location.href });
      if (!r.ok) return this.renderForm(r.error || 'Something went wrong. Please try again.');
      this.office = r.office ?? null;
      if (r.state === 'offline') {
        if (r.offline) this.support = r.offline;
        return this.renderOffline();
      }
      store.set({ id: r.id!, token: r.token! });
      this.renderChat(r.messages ?? []); // includes the automatic "What can we help you with today?"
      this.onStatus({ state: 'ringing' });
    });
    this.screen(form);
  }

  private renderChat(messages: Message[] = []) {
    this.seen.clear();
    const status = el('p', { class: 'status', 'aria-live': 'polite' });
    this.log = el('div', { class: 'log', role: 'log', 'aria-live': 'polite' });
    const input = el('textarea', { rows: '2', maxlength: '2000', placeholder: 'Type your message…', 'aria-label': 'Message' }) as HTMLTextAreaElement;
    const send = el('button', { class: 'btn primary send', type: 'submit' }, 'Send');
    const composer = el('form', { class: 'composer' }, input, send);
    const end = el('button', { class: 'link', type: 'button' }, 'End chat');
    composer.addEventListener('submit', async (e) => {
      e.preventDefault();
      const body = input.value.trim();
      if (!body) return;
      input.value = '';
      this.typing(false);
      const r = await this.emit<{ ok: boolean; error?: string }>('chat:message', { ...store.get(), body });
      if (!r.ok) this.system(r.error || 'Your message could not be sent.');
    });
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        composer.requestSubmit();
      }
    });
    end.addEventListener('click', async () => {
      await this.emit('chat:end', store.get());
      this.onStatus({ state: 'ended' });
    });
    this.typingEl = el('p', { class: 'typing', 'aria-live': 'polite', hidden: '' });
    input.addEventListener('input', () => this.typing(input.value.trim().length > 0));
    this.body.replaceChildren(status, this.log, this.typingEl, composer, el('div', { class: 'foot' }, end));
    messages.forEach((m) => this.addMessage(m));
    input.focus();
  }

  private onStatus(s: { state: string; agentName?: string; support?: Support; office?: string | null }) {
    if (s.support) this.support = s.support;
    if (s.office) this.office = s.office;
    if (s.state === 'offline') {
      store.clear();
      return this.renderOffline();
    }
    if (s.state === 'ended') {
      this.state = 'ended';
      store.clear();
      const again = el('button', { class: 'btn', type: 'button' }, 'Start a new chat');
      again.addEventListener('click', () => this.renderForm());
      this.body.querySelector('.status')?.replaceChildren('Chat ended');
      const composer = this.body.querySelector('.composer');
      composer?.replaceWith(el('div', { class: 'ended' }, el('p', { class: 'muted' }, 'This chat has ended. Thanks for reaching out!'), again));
      this.body.querySelector('.foot')?.remove();
      return;
    }
    const status = this.body.querySelector('.status');
    if (s.state === 'ringing') {
      this.state = 'ringing';
      status?.replaceChildren(el('span', { class: 'dots' }), `Connecting you with ${this.office ? `our ${this.office.replace(/, [A-Z]{2}$/, '')} team` : 'our team'}…`);
    }
    if (s.state === 'active') {
      this.state = 'active';
      this.agentName = s.agentName || this.agentName;
      status?.replaceChildren(`You’re chatting with ${this.agentName || 'our team'}.`);
      if (this.panel.hidden) this.launcher.classList.add('has-news');
    }
  }

  private addMessage(m: Message) {
    if (!this.log || this.seen.has(m.id)) return;
    this.seen.add(m.id);
    const who = m.sender === 'visitor' ? 'You' : m.sender === 'agent' ? (m.agentName || 'Guardian').split(' ')[0] : m.sender === 'bot' ? 'Guardian Access Solutions' : '';
    const bubble = el('div', { class: `msg ${m.sender === 'bot' ? 'agent' : m.sender}` });
    if (who) bubble.append(el('span', { class: 'who' }, who));
    bubble.append(el('p', {}, m.body)); // text node: never parsed as HTML
    this.log.append(bubble);
    this.log.scrollTop = this.log.scrollHeight;
    if (m.sender === 'agent' && this.panel.hidden) this.launcher.classList.add('has-news');
    if (m.sender === 'agent') {
      this.showTyping(false);
      this.alertIfAway();
    }
  }

  /* --------------------------------------------------- Typing & alerts -- */

  /** Tell the agent the visitor is typing — at most every 2 s, and "stopped" after 3 s idle. */
  private typing(active: boolean) {
    const saved = store.get();
    if (!saved || !this.socket) return;
    clearTimeout(this.stopTyping);
    if (active) {
      if (Date.now() - this.sentTyping > 2000) {
        this.socket.emit('typing', { ...saved, typing: true });
        this.sentTyping = Date.now();
      }
      this.stopTyping = window.setTimeout(() => this.typing(false), 3000);
    } else if (this.sentTyping) {
      this.socket.emit('typing', { ...saved, typing: false });
      this.sentTyping = 0;
    }
  }

  private showTyping(on: boolean, name = '') {
    if (!this.typingEl) return;
    clearTimeout(this.typingHide);
    this.typingEl.hidden = !on;
    if (on) {
      this.typingEl.replaceChildren(el('span', { class: 'dots' }), `${name || 'Our team'} is typing…`);
      this.typingHide = window.setTimeout(() => this.showTyping(false), 6000); // in case "stopped" never arrives
    }
  }

  private unlockAudio() {
    try {
      this.audio ??= new AudioContext();
      void this.audio.resume();
    } catch {
      /* no audio */
    }
  }

  /** The visitor switched tabs while waiting: flash the title and play a soft chime. */
  private alertIfAway() {
    if (!document.hidden) return;
    this.unread += 1;
    if (this.audio?.state === 'running') {
      const osc = this.audio.createOscillator();
      const g = this.audio.createGain();
      osc.frequency.value = 740;
      g.gain.setValueAtTime(0.08, this.audio.currentTime);
      g.gain.exponentialRampToValueAtTime(0.001, this.audio.currentTime + 0.4);
      osc.connect(g).connect(this.audio.destination);
      osc.start();
      osc.stop(this.audio.currentTime + 0.41);
    }
    if (this.titleTimer === undefined) {
      this.baseTitle = document.title;
      let on = false;
      this.titleTimer = window.setInterval(() => {
        on = !on;
        document.title = on ? `(${this.unread}) New message — Guardian` : this.baseTitle;
      }, 1000);
      const back = () => {
        if (document.hidden) return;
        clearInterval(this.titleTimer);
        this.titleTimer = undefined;
        this.unread = 0;
        document.title = this.baseTitle;
        document.removeEventListener('visibilitychange', back);
      };
      document.addEventListener('visibilitychange', back);
    }
  }

  private system(text: string) {
    this.log?.append(el('div', { class: 'msg system' }, el('p', {}, text)));
  }

  private async resume(saved: Stored) {
    const r = await this.emit<{ ok: boolean; conversation?: { state: string; office: string | null; agentName: string | null }; messages?: Message[]; support?: Support }>('chat:resume', saved);
    if (!r.ok || !r.conversation || !['ringing', 'active'].includes(r.conversation.state)) {
      store.clear();
      return;
    }
    if (r.support) this.support = r.support;
    this.office = r.conversation.office;
    this.renderChat(r.messages);
    this.onStatus({ state: r.conversation.state, agentName: r.conversation.agentName?.split(' ')[0] });
    this.panel.hidden = false;
    this.launcher.classList.add('is-open');
    this.launcher.setAttribute('aria-expanded', 'true');
  }
}

if (!(window as unknown as { __guardianChat?: boolean }).__guardianChat) {
  (window as unknown as { __guardianChat?: boolean }).__guardianChat = true;
  const boot = () => new GuardianChat();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
}
